/**
 * src/lib/api/categories.js — product_categories CRUD wrappers
 *
 * 2026-04-26 (B5d): inline "Other → suggest a name" affordance creates
 * a new product_categories row from the form. Bilingual integrity per
 * CEO directive: label_en and label_es are filled with the same input
 * until manually translated later (Spanish-speaking team members see
 * the English token rather than nothing). slug is derived deterministically.
 */

import supabase from '../supabase.js';

const SPECIAL = new Set(['general', 'other']);

function toSlug(name) {
  return String(name || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // strip accents
    .replace(/[^a-z0-9]+/g, '_')     // non-alnum → underscore
    .replace(/^_+|_+$/g, '')         // trim
    .slice(0, 60);
}

/**
 * Create a new product category. Idempotent on slug — if a row with the
 * same slug exists (active or archived), it is reactivated and returned
 * rather than duplicated.
 *
 * @param {string} rawName user-typed category name (any language)
 * @returns {Promise<{success: boolean, slug?: string, label?: string, error?: string, code?: string}>}
 */
export async function addProductCategory(rawName) {
  const name = String(rawName || '').trim();
  if (!name) return { success: false, error: 'Empty name', code: 'EMPTY' };

  const slug = toSlug(name);
  if (!slug) return { success: false, error: 'Could not derive slug', code: 'BAD_SLUG' };
  if (SPECIAL.has(slug)) return { success: false, error: 'Reserved slug', code: 'RESERVED' };

  // Reactivate if previously archived; otherwise insert.
  const { data: existing } = await supabase
    .from('product_categories')
    .select('id, slug, label_en, is_active')
    .eq('slug', slug)
    .maybeSingle();

  if (existing) {
    if (existing.is_active) {
      return { success: true, slug: existing.slug, label: existing.label_en };
    }
    const { data, error } = await supabase
      .from('product_categories')
      .update({ is_active: true })
      .eq('slug', slug)
      .select('slug, label_en')
      .maybeSingle();
    if (error) return { success: false, error: error.message, code: 'UPDATE_ERROR' };
    return { success: true, slug: data?.slug, label: data?.label_en };
  }

  // Pick a sort_order at the tail (just before "general" which is 99).
  const { data: maxRow } = await supabase
    .from('product_categories')
    .select('sort_order')
    .lt('sort_order', 99)
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();
  const nextOrder = (maxRow?.sort_order ?? 0) + 1;

  const { data, error } = await supabase
    .from('product_categories')
    .insert({
      slug,
      label_en: name,
      label_es: name, // bilingual integrity per CEO directive — translate later
      sort_order: nextOrder,
      is_active: true,
    })
    .select('slug, label_en')
    .maybeSingle();

  if (error) {
    // Common: RLS denies because user role isn't ceo/admin/pmo.
    const code = /policy/i.test(error.message) ? 'RLS_DENIED' : 'INSERT_ERROR';
    return { success: false, error: error.message, code };
  }
  return { success: true, slug: data?.slug, label: data?.label_en };
}

export const _internals = { toSlug };
